import { fetchAuthSession } from 'aws-amplify/auth';
import cookies, { COOKIE_OPTIONS } from 'config/cookie';
import { JWT_ACCESS_TOKEN } from 'constants/jwt-tokens';

/**
 * Derived rather than imported. Amplify exports `AuthTokens` only from `@aws-amplify/core`, which
 * is not a declared dependency of this package — importing it directly would be an undeclared
 * dependency in a repo that generates an SBOM.
 */
type AuthTokens = NonNullable<
  Awaited<ReturnType<typeof fetchAuthSession>>['tokens']
>;

/**
 * @description Stores the credential the API accepts as its bearer token
 *
 * The API accepts ACCESS tokens only (DLA-1470). An ID token is minted for a client application to
 * establish identity, not for an API, and is refused — presenting one is the token-type confusion
 * the penetration test flagged.
 *
 * Extracted rather than inlined at both call sites for two reasons. `App.tsx` is excluded from
 * coverage and has no test file, so half of this decision would otherwise ship with no automated
 * verification at all; and two copies of one decision can drift, which is the same defect class
 * this ticket closes on the server.
 *
 * Call inside an `if (authSession.tokens)` guard — that narrowing is what lets `accessToken` be
 * read without optional chaining. Amplify types `accessToken` as required and `idToken` as
 * optional, so reading the access token also removes the possibility of writing `undefined` into
 * the cookie.
 *
 * @SADtrace SAD-003-1, SAD-003-1.1
 *
 * @History
 * 08-26-2026 aleksandar dukleski: DLA-1470 created
 *
 * @param {AuthTokens} tokens - Tokens from an active Amplify session
 *
 * @returns {void}
 */
export const setSessionToken = (tokens: AuthTokens): void => {
  cookies.set(JWT_ACCESS_TOKEN, tokens.accessToken.toString(), COOKIE_OPTIONS);
};
